/*
 *  Header leaderboard crawl — the top five agents, from time to time.
 *
 *  An old-TV-ad ticker: all five side by side on one track, entering from the
 *  right and travelling off to the left, once per appearance.
 *
 *  This lives in global chrome, on every page, for every user, which is what
 *  makes it different from the rest of the leaderboard: motion beside the
 *  search box competes with whatever the person is actually doing. So it is a
 *  cameo — one pass a minute — rather than a marquee that never stops.
 *
 *  **Everyone sees it at the same moment.** The schedule is anchored to
 *  absolute wall-clock boundaries derived from the *server* clock, not to each
 *  client's page-load time, so the whole office gets the same pass together
 *  rather than each browser drifting on its own timer. The server clock rather
 *  than the browser's is what makes that hold when someone's laptop is minutes
 *  off.
 *
 *  It publishes nothing new: the same five names are already on everyone's
 *  Home panel.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { TierArt } from "@/components/tiers";

interface TopRow {
  rank: number;
  full_name: string | null;
  avatar_url: string | null;
  score: number;
  tier: string;
}
interface TopBoard {
  now_ms: number;
  period_ms: number;
  rows: TopRow[];
}

const MEDAL = ["🥇", "🥈", "🥉"];

/** Landing inside this much of the next boundary, we wait for the one after.
 *  Costs that one client a single cycle, but keeps the header still while the
 *  page is still painting. */
const SETTLE_MS = 2500;

/** Crawl speed. Duration is derived from the distance rather than fixed, so
 *  the ticker travels at one steady pace whatever the names happen to be —
 *  a fixed duration would visibly speed up on a month of short names. */
const PX_PER_S = 120;

export function LeaderTicker() {
  const [rows, setRows] = useState<TopRow[]>([]);
  // Server clock minus this browser's clock, so the schedule survives a device
  // whose time is wrong.
  const skew = useRef(0);
  const period = useRef(60_000);
  // Bumped per pass; used as the track's key so the animation restarts.
  const [pass, setPass] = useState(0);
  const [live, setLive] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLAnchorElement | null>(null);
  const endTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    api<TopBoard>("/api/leaderboard/top")
      .then((r) => {
        if (!alive) return;
        skew.current = r.now_ms - Date.now();
        period.current = r.period_ms;
        setRows(r.rows);
      })
      // 403 — the agency switched the board off. Say nothing.
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const start = useCallback(() => {
    // Never spend a pass on an unwatched tab: nobody sees it and the next one
    // is minutes away.
    if (document.visibilityState !== "visible") return;
    setLive(true);
    setPass((p) => p + 1);
  }, []);

  // Chained timeouts onto absolute boundaries rather than setInterval: an
  // interval accumulates drift and would slowly slide out of step with
  // everyone else, which is the one thing this must not do.
  useEffect(() => {
    if (rows.length === 0) return;
    let next: number | undefined;

    const schedule = () => {
      const p = period.current;
      const serverNow = Date.now() + skew.current;
      let wait = p - (serverNow % p);
      if (wait < SETTLE_MS) wait += p;
      next = window.setTimeout(() => {
        start();
        schedule();
      }, wait);
    };

    schedule();
    return () => {
      clearTimeout(next);
      clearTimeout(endTimer.current);
    };
  }, [rows, start]);

  // Measure after the track is in the DOM for this pass: the distance is the
  // container width plus the track width, so the row enters fully off the
  // right edge and leaves fully off the left.
  useEffect(() => {
    if (!live || !boxRef.current || !trackRef.current) return;
    const box = boxRef.current.offsetWidth;
    const track = trackRef.current.offsetWidth;
    const distance = box + track;
    // Capped at 80% of the period so a pass can never still be running when
    // the next boundary fires — long names must not make the passes overlap.
    const dur = Math.min(distance / PX_PER_S, (period.current * 0.8) / 1000);
    const el = trackRef.current;
    el.style.setProperty("--crawl-from", `${box}px`);
    el.style.setProperty("--crawl-to", `${-track}px`);
    el.style.setProperty("--crawl-dur", `${dur}s`);
    // Park static rather than snapping away when motion is reduced: same
    // names, no travel. The CSS keeps the animation behind the same query.
    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    endTimer.current = window.setTimeout(
      () => setLive(false),
      still ? 8000 : dur * 1000,
    );
    return () => clearTimeout(endTimer.current);
  }, [live, pass]);

  if (rows.length === 0) return null;

  return (
    <div
      ref={boxRef}
      className="mrd-crawl-box hidden xl:block flex-1 min-w-0 h-9 overflow-hidden mx-2"
      aria-hidden={!live}
    >
      {live && (
        <Link
          key={pass}
          to="/leaderboard"
          title="Open the leaderboard"
          ref={trackRef}
          className="mrd-crawl inline-flex items-center gap-6 h-9 w-max whitespace-nowrap will-change-transform"
        >
          {rows.map((r, i) => (
            <span key={r.rank} className="flex items-center gap-2">
              <span className="text-[13px] leading-none">
                {MEDAL[i] ?? r.rank}
              </span>
              <Avatar
                name={r.full_name || "?"}
                url={r.avatar_url}
                size="xs"
                tier={r.tier}
              />
              <span className="text-[12.5px] text-slate-200">
                {r.full_name}
              </span>
              <TierArt tier={r.tier} px={14} />
              <span className="text-[12px] tabular-nums text-slate-400">
                {r.score.toLocaleString()}
              </span>
            </span>
          ))}
        </Link>
      )}
    </div>
  );
}
